import type { DrillDataConfig } from '~/entities/drillHole'
import DataCache from '~/lib/data-cache'

export class DrillConfigurationManager {
  private _sceneConfigs: DrillDataConfig[]
  private _defaultConfigs: DrillDataConfig[]
  private dataCache: DataCache<DrillDataConfig[]>

  constructor(
    private loaderConfig: { uuid: string | null },
    private maxAge: number = 0,
  ) {
    this._sceneConfigs = []
    this._defaultConfigs = []
    this.dataCache = new DataCache<DrillDataConfig[]>(this.maxAge)
  }

  get sceneConfigs(): readonly DrillDataConfig[] {
    return this._sceneConfigs
  }

  addConfigsForScene(newConfigs: DrillDataConfig[]) {
    const existingIds = new Set(this._sceneConfigs.map(item => item.data_set_id))
    const newConfig = newConfigs.filter(item => !existingIds.has(item.data_set_id))

    this._sceneConfigs = [...this._sceneConfigs, ...newConfig]
  }

  removeConfigsForDataSet(dataSetId: number) {
    this._sceneConfigs = this._sceneConfigs.filter(config => config.data_set_id !== dataSetId)
  }

  async setConfigsForScene(sceneId: number | null, defaultConfigs: DrillDataConfig[]) {
    if (!sceneId) return

    let configs: DrillDataConfig[] = []

    try {
      configs = await this.dataCache.getOrFetch(sceneId, () => this._getServerConfigs(sceneId))
    } catch (error: unknown) {
      configs = []
    }

    this._sceneConfigs = configs.length ? configs : defaultConfigs
    this._defaultConfigs = defaultConfigs
  }

  async updateSceneConfigs(sceneId: number, modelId: number, newConfigs: DrillDataConfig[]) {
    const serverConfigs = await this._getServerConfigs(sceneId)

    const toBeAdded = newConfigs.filter(
      ({ data_set_id, id }) => !serverConfigs.some(config => config.data_set_id === data_set_id && config.id === id),
    )
    const toBeUpdated = newConfigs.filter(({ data_set_id, id }) =>
      serverConfigs.some(config => config.data_set_id === data_set_id && config.id === id),
    )
    const toBeDeleted = serverConfigs.filter(
      ({ data_set_id, id }) => !newConfigs.some(config => config.data_set_id === data_set_id && config.id === id),
    )

    if (toBeDeleted.length) {
      const cameras = await $fetch<any[]>(`/v2/3dmodel/${modelId}/cameras/`, {
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
      })

      const configs = await Promise.all(cameras.map(camera => this._getServerConfigs(camera.id)))
      const toBeDeletedIds = new Set(toBeDeleted.map(x => x.data_set_id))

      configs.flat().forEach(async (config: any) => {
        if (!toBeDeletedIds.has(config.data_set_id)) return

        await $fetch(`/v2/3dmodel/cameras/${config.camera_id}/drill-configurations/${config.id}`, {
          method: 'DELETE',
          ...useFetchSharedOptions(),
          headers: {
            ...useFetchSharedOptions().headers,
            'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
          },
          body: JSON.stringify(config),
        })
      })
    }

    if (toBeAdded.length) {
      await $fetch(`/v2/3dmodel/cameras/${sceneId}/drill-configurations`, {
        method: 'POST',
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
        body: JSON.stringify(toBeAdded.map(({ id, ...rest }) => rest)),
      })
    }

    if (toBeUpdated.length) {
      await $fetch(`/v2/3dmodel/cameras/${sceneId}/drill-configurations`, {
        method: 'PATCH',
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
        body: JSON.stringify(toBeUpdated),
      })
    }
  }

  updateCollarConfig(config: DrillDataConfig) {
    this._updateDrillRadius(config, 'collar')
  }

  updateSurveyConfig(config: DrillDataConfig) {
    this._updateDrillRadius(config, 'survey')
  }

  updateAssayConfig(config: DrillDataConfig) {
    this._updateDrillRadius(config, 'highlight')
  }

  private _updateDrillRadius(activeConfig: DrillDataConfig, key: 'collar' | 'survey' | 'highlight') {
    this._sceneConfigs.forEach(config => {
      if (config.data_set_id === activeConfig.data_set_id) {
        config[key] = activeConfig[key]
      }
    })
  }

  private async _getServerConfigs(sceneId: number) {
    return await $fetch<DrillDataConfig[]>(`/v2/3dmodel/cameras/${sceneId}/drill-configurations`, {
      ...useFetchSharedOptions(),
      headers: {
        ...useFetchSharedOptions().headers,
        'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
      },
    })
  }
}
